import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { InvoiceModel } from '@data-layer/models/entry-summaries';
import { DocumentModel } from '@data-layer/models/documents';
import { CountryModel, CurrencyModel, IncoTermModel, ValidationMessageModel } from '@data-layer/models/common';
import { Observable, ReplaySubject } from 'rxjs';
import { enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { CUSTOM_MASK, HTS_MASK } from '@helpers/custom-validator';
import { DateHelper } from '@helpers/date.helper';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { EntityModel } from '@data-layer/models/entities';
import { enumDropdownActionIconColor, enumDropdownActionMenuMode } from '@shared/form-wrapper/enums';
import { enumDocumentType } from '@data-layer/enums/documents';
import { enumEntityModelCode } from '@app/data-layer/enums/entities';
import { FormHelper } from '@helpers/form.helper';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { takeUntil } from 'rxjs/operators';
import { EsValidationsHelperService } from '../../../services';
import { CodeRepository } from '@data-layer/repositories';
import { CompaniesService } from '@services/companies.service';

@Component({
  selector: 'es-invoice-default-desktop',
  template: `
    <form [formGroup]="invoiceFg" autocomplete="off" (ngSubmit)="submit()">
      <div class="form-row">
        <div class="col-6">
          <app-control-wrapper [control]="invoiceFg.get('invoiceNumber')" [errors]="errors">
            <label for="invoiceNumber" control-label>{{ 'LABELS.INVOICE_NUMBER' | translate }}</label>
            <input
              control-wrapper
              id="invoiceNumber"
              class="form-control"
              formControlName="invoiceNumber"
              [dropSpecialCharacters]="false"
              [patterns]="CUSTOM_MASK"
              [mask]="'F{17}'"
              [appMaskErrorTooltip]="'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL'"
              [maxMaskLength]="17"
              type="text"
            />
          </app-control-wrapper>
        </div>

        <div class="col-6"></div>

        <div class="col-4">
          <app-control-wrapper [control]="invoiceFg.get('currency')" [errors]="errors">
            <label for="currency" control-label>{{ 'LABELS.CURRENCY' | translate }}</label>
            <ng-select
              id="currency"
              class="custom"
              [formControl]="invoiceFg.get('currency')"
              [items]="currencies"
              [bindLabel]="'code'"
              [bindValue]="'code'"
              [clearable]="false"
              control-wrapper
            ></ng-select>
          </app-control-wrapper>
        </div>
        <div class="col-6" *ngIf="invoiceFg.get('currency').value !== DEFAULT_CURRENCY_CODE">
          <div class="form-group">
            <label>{{ 'LABELS.RATE' | translate }}</label>
            <div class="form-control border-unset control-rate">{{ currencyHasRateError ? '' : currencyRate }}</div>
          </div>
        </div>

        <div class="col-6" *ngIf="invoiceFg.get('currency').value === DEFAULT_CURRENCY_CODE"></div>

        <div class="col-6">
          <app-control-wrapper [control]="invoiceFg.get('incoTermsCode')" [errors]="errors">
            <label for="incoterms" control-label>{{ 'LABELS.INCOTERMS' | translate }} {{ 'FORM_LABEL.OPTIONAL' | translate }}</label>
            <ng-select
              id="incoterms"
              class="custom"
              [formControl]="invoiceFg.get('incoTermsCode')"
              [items]="incoterms"
              bindValue="code"
              [placeholder]="'FORM_PLACEHOLDER.SELECT' | translate"
              control-wrapper
              [searchFn]="searchInvoice"
            >
              <ng-template ng-label-tmp let-item="item">
                {{ item.code + ' ' + item.name }}
              </ng-template>
              <ng-template ng-option-tmp let-item="item">
                {{ item.code + ' ' + item.name }}
              </ng-template>
            </ng-select>
          </app-control-wrapper>
        </div>

        <ng-container>
          <div class="col-6"></div>
          <div class="col-6">
            <auto-select
              [errors]="errors"
              [isShowHelper]="true"
              [fc]="invoiceFg.get('countryOfExportId')"
              [id]="'countryOfExportId'"
              [bindValue]="'id'"
              [bindLabel]="['code', 'description']"
              [clearable]="true"
              [label]="'LABELS.COUNTRY_OF_EXPORT'"
              [initializeItems]="initCountryOfExport"
              [change]="getCountries()"
              [helper]="'FORM_HELPER.COUNTRY_OF_EXPORT'"
            ></auto-select>
          </div>
        </ng-container>
      </div>

      <div class="btn-remove" *ngIf="isShowClear" (click)="resetForm()">
        <i class="fa fa-trash btn-remove-icon"></i>
        {{ 'FORM_LABEL.CLEAR_FORM' | translate }}
      </div>

      <div class="sidebar-form-buttons" *ngIf="!isHideFooter">
        <button type="submit" class="btn btn-primary">{{ 'BUTTON.SAVE' | translate }}</button>
      </div>
    </form>

    <missing-fields [code]="'LABELS.ENTRY_SUMMARY'" (decline)="invoiceDataSaved.emit()" (confirm)="confirm()"></missing-fields>
    <custom-modal [header]="'ENTRY_SUMMARY.CHANGE_CURRENCY_SETTINGS_TITLE'" [(open)]="isWarningCurrencyModalOpen">
      <div [innerHTML]="'ENTRY_SUMMARY.CHANGE_CURRENCY_SETTINGS_WARNING' | translate"></div>
      <button footer-action type="button" class="btn btn-primary" (click)="submit()">
        {{ 'BUTTON.CONTINUE' | translate }}
      </button>
      <button footer-action type="button" class="btn btn-secondary" (click)="resetCurrency()">{{ 'BUTTON.CANCEL' | translate }}</button>
    </custom-modal>
  `,
  styleUrls: ['./es-invoice-form-desktop.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsInvoiceFormDesktopComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public invoiceFg: UntypedFormGroup;
  @Input() public currencies: CurrencyModel[] = [];
  @Input() public transportationMode: enumTransportationMode;
  @Input() public entrySummaryId: number;
  @Input() public incoterms: IncoTermModel[] = [];
  @Input() public index: number;
  @Input() public invoice: InvoiceModel;
  @Input() public exportDate: string = DateHelper.now;
  @Input() public allValidationMessages: ValidationMessageModel[] = [];
  @Input() public isHideFooter: boolean;
  @Input() public isShowClear: boolean;
  @Input() public currencyRate: number = 1;
  @Input() public isResetCounty: boolean = true;

  @Output() public invoiceDataSaved: EventEmitter<Partial<InvoiceModel>> = new EventEmitter();

  @ViewChild(MissingFieldsComponent, { static: false })
  public missingFieldModal: MissingFieldsComponent;

  public manufacturer: EntityModel;

  public HTS_MASK: string = HTS_MASK;
  public iconColor: enumDropdownActionIconColor = enumDropdownActionIconColor.DEFAULT;
  public menuMode: enumDropdownActionMenuMode = enumDropdownActionMenuMode.DEFAULT;
  public DEFAULT_CURRENCY_CODE: string = DEFAULT_CURRENCY_CODE;
  public validationMessages: ValidationMessageModel[] = [];
  public manufacturerValidationMessages: ValidationMessageModel[] = [];
  public initCountryOfExport: CountryModel[] = [];

  public totalEnteredValue: number;
  public totalGrossWeight: number;

  public enumEntityModelCode = enumEntityModelCode;
  public enumDocumentType = enumDocumentType;

  public attachedDocuments: DocumentModel[] = [];

  public isCurrencyChanged: boolean = false;
  public isWarningCurrencyModalOpen: boolean = false;

  public CUSTOM_MASK = CUSTOM_MASK;
  public errors = {
    required: 'FORM_ERROR.REQUIRED',
    invoiceNumberMatch: 'FORM_ERROR.INVOICE_NUMBER_MATCH',
    maxlength: 'FORM_ERROR.MAX_LENGTH',
    currencyIsNotAvailable: 'BACK_ERROR.CURRENCY_IS_NOT_AVAILABLE',
  };

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private fh: FormHelper,
    private cd: ChangeDetectorRef,
    private esValidationsHelperService: EsValidationsHelperService,
    private codeRepository: CodeRepository,
    private companiesService: CompaniesService,
  ) {}

  public ngOnInit(): void {
    this.invoiceFg.controls.currency.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.isCurrencyChanged = this.invoice.currency !== value;
    });

    this.initializeCountryOfExport();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.invoice || changes.allValidationMessages) {
      this.getValidationMessages();
      this.getManufactureValidationMessages();
      this.totalEnteredValue = this.getTotalEnteredValue();
      this.totalGrossWeight = this.getTotalGrossWeight();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public searchInvoice(term: string, item: IncoTermModel) {
    term = term.toLowerCase().trim();
    return item.code.toLowerCase().indexOf(term) > -1 || item.name.toLowerCase().indexOf(term) > -1;
  }

  public get currencyHasRateError(): boolean {
    return isNaN(this.currencyRate);
  }

  public resetCurrency(): void {
    this.invoiceFg.get('currency').patchValue(this.invoice.currency || DEFAULT_CURRENCY_CODE);
    this.hideWarningCurrencyModal();
  }

  public submit(): void {
    if (this.isCurrencyChanged) {
      this.openWarningCurrencyModal();
      this.isCurrencyChanged = false;
      this.cd.markForCheck();
      return;
    }
    this.hideWarningCurrencyModal();
    if (this.isValidForm()) {
      this.invoiceDataSaved.emit();
    }
  }

  public setCurrencyRate(val: number): void {
    this.currencyRate = val;
    // TODO: check setCurrencyRate using, not direct logic
    this.cd.detectChanges();
  }

  public confirm(): void {}

  public getCountries(): Observable<CountryModel[]> {
    return this.getCountriesCallApi.bind(this);
  }

  public getCountriesCallApi(code: string): Observable<CountryModel[]> {
    const call: Observable<CountryModel[]> = this.codeRepository.searchCountry(code);

    return call;
  }

  public resetForm(): void {
    this.invoiceFg.get('invoiceNumber').reset();
    this.invoiceFg.get('currency').reset('USD');
    this.invoiceFg.get('incoTermsCode').reset();
    this.invoiceFg.get('countryOfExportId').reset();
    this.invoiceFg.get('countryOfExport').reset();
  }

  private isValidForm(): boolean {
    const invalidFieldsInvoice = this.fh.validateAllFields(this.invoiceFg);

    delete invalidFieldsInvoice.name;
    delete invalidFieldsInvoice.city;
    delete invalidFieldsInvoice.country;
    delete invalidFieldsInvoice.line1;
    delete invalidFieldsInvoice.postalCode;

    if (Object.keys(invalidFieldsInvoice).length) {
      this.openModal(invalidFieldsInvoice);
      this.cd.markForCheck();
      return false;
    }

    return true;
  }

  private getTotalEnteredValue(): number {
    return (this.invoice?.lines || []).reduce((acc, curr) => acc + curr.value, 0);
  }

  private getTotalGrossWeight(): number {
    return (this.invoice?.lines || []).reduce((acc, curr) => acc + curr.grossWeight, 0);
  }

  private getValidationMessages(): void {
    this.validationMessages = this.esValidationsHelperService.getValidationMessages(
      this.allValidationMessages,
      this.invoice,
      this.transportationMode,
    );
  }

  private getManufactureValidationMessages(): void {
    this.manufacturerValidationMessages = this.esValidationsHelperService.getManufactureValidationMessages(
      this.invoice,
      this.transportationMode,
    );
  }

  private openModal(data) {
    if (data.quantity) {
      delete data.quantity;
      data['invoice_total_quantity'] = 'invoice_total_quantity';
    }

    this.missingFieldModal.openCustomModal(data);
  }

  private openWarningCurrencyModal() {
    this.isWarningCurrencyModalOpen = true;
    this.cd.markForCheck();
  }

  private hideWarningCurrencyModal() {
    this.isWarningCurrencyModalOpen = false;
    this.cd.markForCheck();
  }

  private initializeCountryOfExport(): void {
    if (!this.invoiceFg.get('countryOfExportId').value || !this.invoiceFg.get('countryOfExport')?.value?.code) {
      return;
    }

    this.codeRepository
      .getCountry(this.invoiceFg.get('countryOfExport')?.value?.code)
      .pipe(takeUntil(this.destroy$))
      .subscribe((country: CountryModel) => {
        this.initCountryOfExport = [country];
        this.cd.markForCheck();
      });
  }
}
